/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

// GAS
export type GasType = 'gas.Air' | 'gas.CH4' | 'gas.CO2' | 'gas.SF6' | 'gas.Ar' | 'gas.O2' | 'gas.N2' | 'gas.H2';
export type GasProperties = {
  molarMass: number;
  specificHeatCapacity: number;
};
type GasMapping = {
  type: GasType;
  properties: GasProperties;
};

// PSYCHROMETER
export type PsychrometerType = 'psychrometer.Standard' | 'psychrometer.WMO';
export type PsychrometerProperties = {
  psychrometerConstantA: number;
  psychrometerConstantB: number;
};
type PsychrometerMapping = {
  type: PsychrometerType;
  properties: PsychrometerProperties;
};

export type ConditionType = GasType | PsychrometerType;
type ConditionMapping = GasMapping | PsychrometerMapping;

class CalculatorConditions {
  private static _instance: CalculatorConditions;
  public static get instance() {
    return this._instance || (this._instance = new this());
  }

  private gasMappings: GasMapping[] = [
    { type: 'gas.Air', properties: { molarMass: 28.96443, specificHeatCapacity: 1.006 } },
    { type: 'gas.CH4', properties: { molarMass: 16.043, specificHeatCapacity: 2.191 } },
    { type: 'gas.CO2', properties: { molarMass: 44.01, specificHeatCapacity: 0.839 } },
    { type: 'gas.SF6', properties: { molarMass: 146.055, specificHeatCapacity: 0.667 } },
    { type: 'gas.Ar', properties: { molarMass: 39.948, specificHeatCapacity: 0.5203 } },
    { type: 'gas.O2', properties: { molarMass: 31.999, specificHeatCapacity: 0.918 } },
    { type: 'gas.N2', properties: { molarMass: 28.013, specificHeatCapacity: 1.04 } },
    { type: 'gas.H2', properties: { molarMass: 2.016, specificHeatCapacity: 14.3 } },
  ];

  private psychrometerMappings: PsychrometerMapping[] = [
    { type: 'psychrometer.WMO', properties: { psychrometerConstantA: 0.000662, psychrometerConstantB: 0.00115 } },
    { type: 'psychrometer.Standard', properties: { psychrometerConstantA: 0.000585, psychrometerConstantB: 0 } },
  ];

  public propertiesForPsychrometer = (psychrometer: PsychrometerType) => this.propertiesForCondition(psychrometer) as PsychrometerProperties;
  public propertiesForGas = (gas: GasType) => this.propertiesForCondition(gas) as GasProperties;

  private propertiesForCondition(condition: ConditionType) {
    let mapping;
    try {
      const mappings = condition.indexOf('gas') >= 0 ? this.gasMappings : this.psychrometerMappings;
      mapping = (mappings as ConditionMapping[]).find((m) => m.type === condition);
    } catch (e) {
      throw new TypeError(`CalculatorConditions.propertiesForCondition(): invalid condition type: ${condition}`);
    }
    if (!mapping) {
      throw new TypeError(`CalculatorConditions.propertiesForCondition(): invalid condition type: ${condition}`);
    }
    return mapping.properties;
  }
}

export default CalculatorConditions.instance;
